import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';

// 会话历史订单展开状态，按 conversationId 记录，限制最多 1000 条
const orderHistoryVisibleState = atom<Record<string, boolean>>({});

const setOrderHistoryVisible = (
  prev: Record<string, boolean>,
  conversationId: string,
): Record<string, boolean> => {
  const nextValue = !(prev[conversationId] ?? false);
  const hasKey = Object.prototype.hasOwnProperty.call(prev, conversationId);

  // 常规路径：已有该 key 或数量未达上限，直接浅拷贝
  if (hasKey) {
    return {
      ...prev,
      [conversationId]: nextValue,
    };
  }

  const size = Object.keys(prev).length;
  if (size < 1000) {
    return {
      ...prev,
      [conversationId]: nextValue,
    };
  }

  // 超出上限时，移除最早的一条
  const next: Record<string, boolean> = {};
  let isFirst = true;
  // 这里依赖对象的枚举顺序来近似“最早写入”的 key
  // 在大多数场景下足够作为简单 LRU 上限淘汰
  for (const key in prev) {
    if (!Object.prototype.hasOwnProperty.call(prev, key)) continue;
    if (isFirst) {
      isFirst = false;
      continue;
    }
    next[key] = prev[key];
  }

  next[conversationId] = nextValue;
  return next;
};

export const useOrderHistoryVisible = (conversationId?: string) => {
  const [historyVisibleByConversationId, setHistoryVisibleByConversationId] =
    useAtom(orderHistoryVisibleState);

  const historyVisible = conversationId
    ? historyVisibleByConversationId[conversationId] ?? false
    : false;

  const toggleHistoryVisible = useCallback(() => {
    if (!conversationId) return;
    setHistoryVisibleByConversationId((prev) => setOrderHistoryVisible(prev, conversationId));
  }, [conversationId, setHistoryVisibleByConversationId]);

  return {
    historyVisible,
    toggleHistoryVisible,
  };
};
